import commerceConfig from '@commerce/config'
import usePrice, { formatPrice } from '@commerce/product/use-price'
import type { ProductVariant } from '@commerce/types/product'
import CalculatorButton from '@components/paintCalculator/CalculatorButton'
import { Button, useUI } from '@components/ui'
import processLocale from '@lib/locale'
import { calculatePaintNeeded } from '@lib/paint'
import { areaDisplayText } from '@lib/products'
import convert from 'convert'
import { useTranslation } from 'next-i18next'
import { useRouter } from 'next/router'
import { FC, useState } from 'react'
import GardenPotModalButton from '../GardenPotModalButton'
import ProductOptionByLitre from '../ProductOptions/ProductOptionByLitre'
import { SelectedOptions } from '../helpers'
interface props {
  addToCart: () => void
  loading: boolean
  surfaceArea: number
  options: ProductVariant[]
  currencyCode: string
  selectedOptions: SelectedOptions
  setSelectedOptions: React.Dispatch<React.SetStateAction<SelectedOptions>>
  totalLitres?: number
  isByLitreProcess?: boolean
  isGardenPotPaint?: boolean
  isSamplePotPaint?: boolean
  prismicContent?: any
  numberOfCoats: string
  totalPrice?: number
  disabledAmountSelection?: boolean
}

const ProductPaintSelection: FC<props> = ({
  addToCart,
  loading,
  surfaceArea,
  options,
  currencyCode,
  setSelectedOptions,
  totalLitres,
  isByLitreProcess,
  isGardenPotPaint,
  isSamplePotPaint,
  prismicContent,
  numberOfCoats,
  totalPrice,
  selectedOptions,
  disabledAmountSelection,
}) => {
  const { calculatorUnit, paintCoverageCalculated, calculatedCoverage } =
    useUI()
  const { t, i18n } = useTranslation('product')
  const { t: tcommon } = useTranslation('common')
  const [selectedOption, setSelectedOption] = useState<ProductVariant | null>(
    null
  )
  const { locale } = useRouter()
  const {
    config: { context },
  } = commerceConfig
  const currentLocale = processLocale(locale)
  const currentContext = context?.[currentLocale]
  const taxAmount = currentContext?.taxAmount

  const { price: beforeTaxTotal } = usePrice({
    amount: totalPrice!,
    currencyCode: currencyCode!,
    suffix: `(${tcommon('excl-vat')})`,
  })

  const { price: afterTaxTotal } = usePrice({
    amount: totalPrice ? totalPrice * (1 + taxAmount || 1) : 0,
    currencyCode: currencyCode!,
    suffix: `(${tcommon('incl-vat-approx')})`,
  })

  const paintNeeded =
    paintCoverageCalculated && surfaceArea && calculatedCoverage
      ? calculatePaintNeeded(calculatedCoverage, calculatorUnit, surfaceArea)
      : 0

  const costPerAreaUnit =
    totalPrice! /
    (calculatorUnit == 'metres'
      ? surfaceArea * totalLitres!
      : Math.floor(convert(surfaceArea * totalLitres!, 'm2').to('sq ft')))

  // const totalQtyForSelectedOptions = Object.values(selectedOptions).reduce(
  //   (acc, currentValue) => {
  //     return Number(acc) + Number(currentValue)
  //   },
  //   0
  // )

  return (
    <>
      {isByLitreProcess && disabledAmountSelection ? (
        <></>
      ) : (
        <div className="flex h-full flex-col justify-between ">
          <div>
            <div className="flex items-center justify-between">
              <div className="body-1-bold text-ui-dark-grey">
                {t('select-amount')}:
              </div>

              {isGardenPotPaint ? (
                <GardenPotModalButton prismicContent={prismicContent} />
              ) : (
                <CalculatorButton
                  displayText={t('how-much-do-i-need').toString()}
                  prismicContent={prismicContent}
                />
              )}
            </div>
            <div className="mt-4">
              <ProductOptionByLitre
                options={options}
                currencyCode={currencyCode}
                selectedOption={selectedOption}
                selectedOptions={selectedOptions}
                setSelectedOption={setSelectedOption}
                setSelectedOptions={setSelectedOptions}
                disabledAmountSelection={disabledAmountSelection}
                isByLitreProcess={isByLitreProcess || false}
              />
            </div>
          </div>

          <div className="my-8 h-px w-full bg-ui-grey-25" />
          <div className="flex flex-col">
            <div className="label flex flex-col gap-y-3 pb-8">
              <div className="flex justify-between">
                <div className="font-bold uppercase">
                  {tcommon('amount-selected')}
                </div>

                <div>{totalLitres ? `${totalLitres} L` : '-'}</div>
              </div>
              {!isGardenPotPaint && !isSamplePotPaint ? (
                <>
                  <div className="flex justify-between">
                    <div className="font-bold uppercase">
                      {t('coverage-calculated')}
                    </div>
                    <div>
                      {paintNeeded > 0 ? (
                        <span className="bg-ui-light-grey p-1">
                          {areaDisplayText(calculatedCoverage, calculatorUnit)}
                        </span>
                      ) : (
                        '-'
                      )}
                    </div>
                  </div>
                  <div className="flex justify-between">
                    <div className="font-bold uppercase">{t('coverage')}</div>
                    {totalLitres && totalLitres > 0 ? (
                      <div>
                        {t('approximately')}
                        <span className="font-bold">
                          {calculatorUnit == 'metres' &&
                            ` ${surfaceArea * totalLitres!} m² `}
                          {calculatorUnit == 'feet' &&
                            ` ${Math.floor(
                              convert(surfaceArea * totalLitres!, 'm2').to(
                                'sq ft'
                              )
                            )} ft² `}
                        </span>
                        {t('with')} {numberOfCoats}{' '}
                        {numberOfCoats == 'one'
                          ? `${t('coat')}`
                          : `${t('coats')}`}
                        *
                      </div>
                    ) : (
                      <>{`(${t('automatically-calculated')})`}</>
                    )}
                  </div>
                  <div className="flex justify-between">
                    <div className="font-bold uppercase">
                      {t('cost')}/{areaDisplayText(0, calculatorUnit)}
                    </div>
                    <div className="flex flex-col text-right">
                      {costPerAreaUnit && costPerAreaUnit > 0
                        ? `${formatPrice({
                            amount: costPerAreaUnit,
                            currencyCode,
                            locale,
                          })}/${areaDisplayText(0, calculatorUnit)}`
                        : `(${t('automatically-calculated')})`}
                    </div>
                  </div>
                </>
              ) : null}
              {totalPrice !== 0 && totalPrice ? (
                <>
                  <div className="flex justify-between">
                    <div className="font-bold uppercase">
                      {tcommon('total')}
                    </div>
                    <div className="flex flex-col text-right">
                      {locale?.endsWith('eu') ? afterTaxTotal : ''}
                      <span>{`${beforeTaxTotal} ${
                        locale?.endsWith('gb') ? `(incl. GST / VAT)` : ''
                      }`}</span>
                    </div>
                  </div>
                </>
              ) : null}
            </div>
            {i18n.exists('incl-vat-approx-detail') && (
              <div className="body-2 pb-4">{`${tcommon(
                'incl-vat-approx-detail'
              )}`}</div>
            )}
            <Button
              aria-label={`${t('add-to-cart')}`}
              type="button"
              width="100%"
              onClick={addToCart}
              loading={loading}
              disabled={totalPrice === 0 || loading}
            >
              {totalPrice == 0
                ? `${tcommon('make-your-selection')}`
                : `${tcommon('add-to-cart')}`}
            </Button>
          </div>
        </div>
      )}
    </>
  )
}

export default ProductPaintSelection
